import { DataProvider, HttpError } from 'react-admin';

import { fetchUserRoles } from './fetchUserRoles';

export interface UserRoleRecord {
  id: string;
  name: string;
  code: string;
}

export interface UserRolesDataProvider extends DataProvider<'userRoles'> {}

/* There are some "any" assertions in return data to satisfy the generic approach to using the React Admin dataProvider. Without these,
React Admin will try to warn us that a dataProvider method could be called with another resource method. When calling methods explicitly,
use type parameters for type safety:
dataProvider.getList<UserRoleRecord>('userRoles', params) returns Promise<GetListResult<UserRoleRecord>>
*/
export const userRolesDataProvider: UserRolesDataProvider = {
  getList: async (resource, params) => {
    try {
      const { partnerCode } = params.filter || {};

      const uuid = sessionStorage.getItem('uuid');
      if (!uuid) {
        throw new HttpError('User UUID not found in session', 401);
      }

      const allRoles = await fetchUserRoles(uuid);

      const filtered = partnerCode ?
        allRoles.filter(role => role.partnerCodes.includes(partnerCode)) :
        allRoles;

      const data: UserRoleRecord[] = filtered
        .map(role => ({
          id: role.code,
          name: role.name,
          code: role.code,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));

      return {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        data: data as any,
        total: data.length,
      };
    } catch (error) {
      console.error('userRolesDataProvider.getList failed:', {
        resource,
        params,
        error: error instanceof Error ? error.message : error,
      });
      const status = (error as { status?: number }).status || 500;
      throw new HttpError('Failed to load user roles.', status);
    }
  },

  getOne: async () => {
    throw new Error('getOne not implemented for userRoles resource');
  },

  getMany: async () => {
    throw new Error('getMany not implemented for userRoles resource');
  },

  getManyReference: async (resource, params) => {
    // ReferenceManyField passes target (field name) and id (field value)
    // We delegate to getList with the appropriate filter
    const filter = { [params.target]: params.id };
    return userRolesDataProvider.getList(resource, { ...params, filter });
  },

  create: async () => {
    throw new Error('create not implemented for userRoles resource');
  },

  update: async () => {
    throw new Error('update not implemented for userRoles resource');
  },

  updateMany: async () => {
    throw new Error('updateMany not implemented for userRoles resource');
  },

  delete: async () => {
    throw new Error('delete not implemented for userRoles resource');
  },

  deleteMany: async () => {
    throw new Error('deleteMany not implemented for userRoles resource');
  },
};

export default userRolesDataProvider;
